import { ExternalLink } from 'lucide-react'
import { useContext } from 'react'
import { useLocalStorage } from 'usehooks-ts'

import BrokersMemberContext from 'components/context/BrokersMemberContext'
import DisplayTooltipOnHover from 'components/reusable/DisplayTooltipOnHover'
import { OpacityTransitionEnterAndLeave } from 'components/reusable/OpacityTransition'
import { SimpleFieldRow } from 'components/reusable/SimpleFieldRow'
import { Badge, Banner } from 'slab'
import { formatMoney, getFormattedDate } from 'utils'
import { useGetBrokersMembersMemberIdIchraData } from 'utils/web_api'
import { type BrokersICHRADataQmeStatus } from 'utils/web_api.schemas'

import { SensitiveFieldRow } from './SensitiveFieldRow'
import { CollapsibleDataContainer } from '../reusable/CollapsibleDataContainer'
import { SkeletonItem } from '../reusable/SkeletonItem'

function getFormattedDateText (dateString?: string | null) {
  if (dateString === undefined || dateString === null) {
    return dateString
  }

  return getFormattedDate(dateString, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

function getQmeStatusText (
  qmeStatus?: BrokersICHRADataQmeStatus | null,
  hasHsaEligibleEnrollment?: boolean
) {
  if (!qmeStatus) {
    return qmeStatus
  }

  let text = qmeStatus.replace('_', ' ')
    .replace(/\b\w/g, (l: string) => l.toUpperCase())

  if (hasHsaEligibleEnrollment) {
    text += ' (member is enrolled in an HSA-eligible plan)'
  }

  return text
}

const Heading = ({
  hasHsaEligibleEnrollment,
  ichraNoticeUrl,
  allowance,
  qmeStatus,
  isCollapsed,
}: {
  hasHsaEligibleEnrollment?: boolean
  ichraNoticeUrl?: string
  allowance?: string | null
  qmeStatus?: BrokersICHRADataQmeStatus
  isCollapsed?: boolean
}) => (
  <div className='flex items-center justify-between'>
    <div className='flex items-center space-x-2'>
      <span className='heading-md'>ICHRA and affordability</span>
      {ichraNoticeUrl && (
        <a
          href={ichraNoticeUrl}
          target='_blank'
          rel='noopener noreferrer'
          onClick={(e) => e.stopPropagation()}
          className='no-underline'
        >
          <Badge type='info' leftIcon={ExternalLink}>
            View notice
          </Badge>
        </a>
      )}
      <OpacityTransitionEnterAndLeave
        visible={Boolean(isCollapsed && allowance)}
      >
        <DisplayTooltipOnHover text='Allowance'>
          <Badge type='neutral'>
            {allowance}
          </Badge>
        </DisplayTooltipOnHover>
      </OpacityTransitionEnterAndLeave>
      <OpacityTransitionEnterAndLeave
        visible={Boolean(isCollapsed && qmeStatus)}
      >
        <DisplayTooltipOnHover text='QME Status'>
          <Badge type={qmeStatus === 'all_allowed' ? 'success' : 'neutral'}>
            QME {getQmeStatusText(qmeStatus, hasHsaEligibleEnrollment)}
          </Badge>
        </DisplayTooltipOnHover>
      </OpacityTransitionEnterAndLeave>
    </div>
  </div>
)

const SkeletonFieldRow = ({
  labelWidth = 'w-1/3',
  valueWidth = 'w-3/4',
}: {
  labelWidth?: string
  valueWidth?: string
}) => (
  <div className='px-4 py-2 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0'>
    <dt className='text-sm font-medium leading-6 text-gray-900'>
      <SkeletonItem widthClass={labelWidth} />
    </dt>
    <dd className='mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0'>
      <SkeletonItem widthClass={valueWidth} />
    </dd>
  </div>
)

const widths = ['w-1/4', 'w-1/3', 'w-2/5', 'w-3/5', 'w-4/5', 'w-1/2', 'w-3/4']
const getRandomWidth = () => {
  return widths[Math.floor(Math.random() * widths.length)]
}

const MemberIchraAffordabilitySkeleton = () => (
  <dl className='divide-y divide-gray-100 p-4'>
    <SkeletonFieldRow
      labelWidth={getRandomWidth()}
      valueWidth={getRandomWidth()}
    />
    <SkeletonFieldRow
      labelWidth={getRandomWidth()}
      valueWidth={getRandomWidth()}
    />
    <SkeletonFieldRow
      labelWidth={getRandomWidth()}
      valueWidth={getRandomWidth()}
    />
    <SkeletonFieldRow
      labelWidth={getRandomWidth()}
      valueWidth={getRandomWidth()}
    />
    <SkeletonFieldRow
      labelWidth={getRandomWidth()}
      valueWidth={getRandomWidth()}
    />
    <SkeletonFieldRow
      labelWidth={getRandomWidth()}
      valueWidth={getRandomWidth()}
    />
    <SkeletonFieldRow
      labelWidth={getRandomWidth()}
      valueWidth={getRandomWidth()}
    />
    <SkeletonFieldRow
      labelWidth={getRandomWidth()}
      valueWidth={getRandomWidth()}
    />
  </dl>
)

interface MemberIchraAffordabilityProps {
  memberId: string
}

export default function MemberIchraAffordability ({
  memberId,
}: MemberIchraAffordabilityProps) {
  const { planYear } = useContext(BrokersMemberContext)
  const [isCollapsed] = useLocalStorage('member-ichra-affordability', true)

  const {
    data: ichraData,
    isFetching,
    error,
  } = useGetBrokersMembersMemberIdIchraData(
    memberId,
    {
      plan_year: planYear,
    },
    {
      query: {
        enabled: !!memberId && !!planYear,
        refetchOnWindowFocus: false,
      },
    }
  )

  const ichraAllowance = ichraData?.ichra_allowance && ichraData.ichra_allowance.join(', ')

  const successContent = (
    <dl className='divide-y divide-gray-100 p-4'>
      <SimpleFieldRow
        label='Notice date'
        omitIfUndefined
        value={getFormattedDateText(ichraData?.notice_date)}
      />

      <SimpleFieldRow
        label='Estimated coverage start date'
        omitIfUndefined
        value={getFormattedDateText(ichraData?.coverage_start_date)}
      />

      <SimpleFieldRow
        label="Employer's coverage start date"
        omitIfUndefined
        value={getFormattedDateText(ichraData?.employer_coverage_start_date)}
      />

      <SimpleFieldRow
        label='Allowance'
        omitIfUndefined
        value={ichraAllowance}
      />

      <SimpleFieldRow
        label='QME Status'
        omitIfUndefined
        value={getQmeStatusText(ichraData?.qme_status, ichraData?.has_hsa_eligible_enrollment)}
      />

      <SensitiveFieldRow
        label='Reported household income'
        omitIfUndefined
        value={
          ichraData?.household_income
            ? formatMoney(parseInt(ichraData.household_income.value || '0'))
            : ichraData?.household_income?.value // Gives us either undefined or null
        }
      />

      <SimpleFieldRow
        label='Estimated to be affordable'
        omitIfUndefined
        value={ichraData?.ichra_affordable}
      />

      <SensitiveFieldRow
        enableCopyButton
        isHidden
        label='Account number'
        omitIfUndefined
        value={ichraData?.deposit_account_number}
      />

      <SensitiveFieldRow
        enableCopyButton
        isHidden
        label='Routing number'
        omitIfUndefined
        value={ichraData?.deposit_routing_number}
      />
    </dl>
  )

  return (
    <CollapsibleDataContainer
      heading={
        <Heading
          hasHsaEligibleEnrollment={ichraData?.has_hsa_eligible_enrollment}
          ichraNoticeUrl={ichraData?.ichra_notice_url}
          allowance={ichraAllowance}
          qmeStatus={ichraData?.qme_status}
          isCollapsed={!isCollapsed}
        />
      }
      isLoading={isFetching}
      error={!!error || !ichraData}
      localStorageKey='member-ichra-affordability'
      loadingContent={<MemberIchraAffordabilitySkeleton />}
      errorContent={
        <Banner
          type='critical'
          title='Error loading ICHRA and affordability information'
        />
      }
      successContent={successContent}
    />
  )
}
